import React, { useMemo } from 'react';
import styled from 'libs/styled';
import { useTranslation } from 'react-i18next';
import { media } from '@fogia/core-web/utils/mediaqueries';
import Button from 'components/Button';
import Text from 'components/Text';
import type { ProductSliderContentGroup } from 'libs/sanity/types/generated';

export type ProductSliderGroupItem = { key: string } & ProductSliderContentGroup;

export const PRODUCT_SLIDER_HEADER_TYPOGRAPHY: Array<string | null> = [
    'Primary/Mobile/18_120_510_-3',
    null,
    null,
    'Primary/15_120_510_-1',
];

const GroupButtonRow = styled('div')`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    row-gap: 4px;
`;

const GroupItem = styled('div')`
    display: inline-flex;
    align-items: baseline;
`;

const groupButtonForwardProps = {
    shouldForwardProp: (prop: string) => prop !== '$isActive',
};

const ProductSliderGroupButton = styled(Button, groupButtonForwardProps)<{ $isActive: boolean }>`
    letter-spacing: inherit;

    && {
        color: ${({ $isActive }) => ($isActive ? 'var(--button-tertiery-text-active)' : undefined)};
    }

    ${media.hover} {
        &:hover {
            color: ${({ $isActive }) => ($isActive ? 'var(--button-tertiery-text-active)' : undefined)};
        }
    }
`;

const GroupLabelComma = styled(Text)`
    margin-right: 6px;
`;

export interface ProductSliderGroupNavProps {
    groups: ProductSliderGroupItem[];
    activeGroupKey: string | null;
    onSelectGroup: (groupKey: string) => void;
}

/** Group labels in the product slider header: multiple groups as text buttons, or a single title. */
export const ProductSliderGroupNav: React.FC<ProductSliderGroupNavProps> = ({
    groups,
    activeGroupKey,
    onSelectGroup,
}) => {
    const { t } = useTranslation();

    const groupRowAriaLabel = t('productSlider.groupLabel', { defaultValue: 'Product groups' });
    const displayGroups = useMemo(
        () => groups.filter((group) => (group.groupTitle?.trim() ?? '').length > 0),
        [groups],
    );

    if (displayGroups.length > 1) {
        return (
            <GroupButtonRow role="group" aria-label={groupRowAriaLabel}>
                {displayGroups.map((group, index) => {
                    const isActive = group.key === activeGroupKey;
                    const label = group.groupTitle?.trim() ?? '';
                    const hasTrailingComma = index < displayGroups.length - 1;
                    return (
                        <GroupItem key={group.key}>
                            <ProductSliderGroupButton
                                type="button"
                                theme="textButton"
                                typography={PRODUCT_SLIDER_HEADER_TYPOGRAPHY}
                                $isActive={isActive}
                                aria-pressed={isActive}
                                onClick={() => onSelectGroup(group.key)}
                            >
                                {label}
                            </ProductSliderGroupButton>
                            {hasTrailingComma ? (
                                <GroupLabelComma
                                    $as="span"
                                    typography={PRODUCT_SLIDER_HEADER_TYPOGRAPHY}
                                    m="0"
                                    color={isActive ? 'var(--button-tertiery-text-active)' : 'inherit'}
                                >
                                    ,
                                </GroupLabelComma>
                            ) : null}
                        </GroupItem>
                    );
                })}
            </GroupButtonRow>
        );
    }

    if (displayGroups.length === 1) {
        const singleGroupTitle = displayGroups[0].groupTitle?.trim() ?? '';
        if (!singleGroupTitle) {
            return null;
        }
        return (
            <Text $as="h3" typography={PRODUCT_SLIDER_HEADER_TYPOGRAPHY} m="0">
                {singleGroupTitle}
            </Text>
        );
    }

    return null;
};
